import { useClientPortalSubmit } from "@certn/api";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
} from "@certn/cascadia-react";
import { showToast } from "@certn/cascadia";
import { Text } from "@certn/cascadia";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, type ReactElement } from "react";
import { components } from "packages/api/generated-schemas/client-portal";
import { CancelCaseModal } from "../components/CancelCaseModal";

import { useCanViewCaseDetails } from "../../../components/permissions/Permissions";

interface Props {
  id: string;
  applicant: string;
  applicantEmail: string;
  status: components["schemas"]["ClientPortalOverallStatus"];
  canResendInvite?: boolean;
}
export function ActionsDropdown(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientPortalEnableCancelCase } = useFlags();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [inviteResent, setInviteResent] = useState(false);
  const canViewCaseDetails = useCanViewCaseDetails();
  const {
    id,
    applicant,
    applicantEmail,
    status,
    canResendInvite = false,
  } = props;
  const isCancelDisabled =
    ["COMPLETE", "CANCELLED"].includes(status.name) ||
    !clientPortalEnableCancelCase;

  const { submit: resendInvite, isSubmitting: isResendingInvite } =
    useClientPortalSubmit(
      `/api/internal/client-portal/case/${id}/resend-invite/`,
      {
        showToastOnError: true,
        onSuccess: () => {
          setInviteResent(true);
          showToast(t("Invite successfully resent"), { type: "success" });
        },
      }
    );

  const disabledKeys = [
    ...(isResendingInvite || inviteResent ? ["resend-invite"] : []),
  ];

  // Array children need explicit keys when passed into MenuSection as `{items}`.
  const items: ReactElement[] = [];

  if (canViewCaseDetails) {
    items.push(
      <MenuItem
        key="view-details"
        id="view-details"
        testID="case-listing-view-details-button"
      >
        <Text style="input/text-field">{t("View details")}</Text>
      </MenuItem>
    );
    items.push(
      <MenuItem
        key="open-new-tab"
        id="open-new-tab"
        testID="case-listing-open-in-new-tab-button"
      >
        <Text style="input/text-field">{t("Open in a new tab")}</Text>
      </MenuItem>
    );
  }

  if (canResendInvite) {
    items.push(
      <MenuItem
        key="resend-invite"
        id="resend-invite"
        testID="case-listing-resend-invite-button"
      >
        <Text style="input/text-field">{t("Resend Invite")}</Text>
      </MenuItem>
    );
  }

  if (!isCancelDisabled) {
    items.push(
      <MenuItem
        key="cancel-case"
        id="cancel-case"
        testID="case-listing-cancel-case-button"
      >
        <Text style="input/text-field">{t("Cancel check")}</Text>
      </MenuItem>
    );
  }

  if (items.length === 0) return null;

  return (
    <>
      <DropdownMenu
        disabledKeys={disabledKeys}
        triggerButton={
          <GhostIconButton
            testID="case-listing-item-expand-button"
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        onItemAction={(menuItem) => {
          const isInDraft = status.name === "DRAFT";
          switch (menuItem) {
            case "view-details":
              if (isInDraft) {
                navigate(`/quickscreen/${id}`);
              } else {
                navigate(`/case-details/${id}`, {
                  state: {
                    last: { href: `${location.pathname}${location.search}` },
                  },
                });
              }
              break;
            case "open-new-tab":
              window.open(
                isInDraft ? `/quickscreen/${id}` : `/case-details/${id}`,
                "_blank"
              );
              break;
            case "resend-invite":
              void resendInvite();
              break;
            case "cancel-case":
              setIsCancelModalOpen(true);
              break;
          }
        }}
        placement="bottom right"
      >
        <MenuSection>{items}</MenuSection>
      </DropdownMenu>
      <CancelCaseModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        caseId={id}
        applicant={applicant}
        applicantEmail={applicantEmail}
        status={status}
      />
    </>
  );
}
